import React, { Suspense, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
// These four components don't affect first paint. Two are no-ops, one runs
// a Query for site settings on mount, one syncs canonical/hreflang tags —
// all safe to defer past FCP so they don't sit in the critical chunk.
const TypographyController = React.lazy(() =>
  import("@/components/site/TypographyController").then((m) => ({ default: m.TypographyController })),
);
const CanonicalHreflangInjector = React.lazy(() =>
  import("@/components/site/CanonicalHreflangInjector").then((m) => ({ default: m.CanonicalHreflangInjector })),
);
import { I18nProvider } from "@/components/site/I18nProvider";
import { jsonLdScript } from "@/lib/jsonld";
import "@/lib/i18n";

// Defer non-critical UI to keep TBT low on mobile
const Toaster = React.lazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })),
);
const SuccessNotificationPopup = React.lazy(() =>
  import("@/components/site/SuccessNotificationPopup").then((m) => ({
    default: m.SuccessNotificationPopup,
  })),
);
const InvoiceThankYouPopup = React.lazy(() =>
  import("@/components/site/InvoiceThankYouPopup").then((m) => ({
    default: m.InvoiceThankYouPopup,
  })),
);
const CookieConsent = React.lazy(() => import("@/components/site/CookieConsent"));

import appCss from "../styles.css?url";

function NotFoundComponent() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">{t("notFound.title")}</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {t("notFound.desc")}
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            {t("notFound.goHome")}
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  const { t } = useTranslation();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          {t("errorPage.title")}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {t("errorPage.desc")}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            {t("errorPage.tryAgain")}
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            {t("errorPage.goHome")}
          </a>
        </div>
      </div>
    </div>
  );
}

const SITE_URL = "https://onlinetaxvat.com";
const SITE_NAME = "Online Tax VAT Bangladesh";
const SITE_LOGO = `${SITE_URL}/pwa/icon-512x512.png?v=5`;
// Lightweight modern-format variant used only for the image preload hint.
// Schema.org JSON-LD keeps the PNG URL for crawler compatibility.
const SITE_LOGO_PRELOAD = `${SITE_URL}/pwa/icon-512x512.webp?v=5`;
const SITE_DESCRIPTION =
  "Bangladesh online tax and VAT service for income tax return filing, VAT registration, TIN certificates, trade license and NBR-aligned compliance support.";

const postalAddress = {
  "@type": "PostalAddress",
  streetAddress: "Dhaka",
  addressLocality: "Dhaka",
  addressRegion: "Dhaka Division",
  postalCode: "1200",
  addressCountry: "BD",
};

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: SITE_NAME,
  legalName: SITE_NAME,
  alternateName: ["Online Tax VAT", "Online Tax & VAT", "অনলাইন ট্যাক্স ভ্যাট"],
  url: SITE_URL,
  logo: {
    "@type": "ImageObject",
    url: SITE_LOGO,
    width: 512,
    height: 512,
  },
  image: SITE_LOGO,
  description: SITE_DESCRIPTION,
  address: postalAddress,
  areaServed: { "@type": "Country", name: "Bangladesh" },
  knowsLanguage: ["en", "bn"],
  brand: {
    "@type": "Brand",
    "@id": `${SITE_URL}/#brand`,
    name: "Online Tax VAT",
    url: SITE_URL,
    logo: SITE_LOGO,
  },
  sameAs: [
    "https://www.facebook.com/onlinetaxvat",
    "https://www.linkedin.com/company/onlinetaxvat",
    "https://www.youtube.com/@onlinetaxvat",
    "https://www.instagram.com/onlinetaxvat",
    "https://x.com/onlinetaxvat",
    "https://www.tiktok.com/@onlinetaxvat",
  ],
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: "+8801891921404",
      contactType: "customer support",
      areaServed: "BD",
      availableLanguage: ["en", "bn"],
    },
  ],
  telephone: "+8801891921404",
};

const professionalServiceSchema = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "@id": `${SITE_URL}/#professionalservice`,
  name: "Online Tax VAT",
  url: SITE_URL,
  logo: SITE_LOGO,
  image: SITE_LOGO,
  description: SITE_DESCRIPTION,
  address: postalAddress,
  areaServed: { "@type": "Country", name: "Bangladesh" },
  serviceArea: { "@type": "Country", name: "Bangladesh" },
};

const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  name: SITE_NAME,
  alternateName: "Online Tax VAT",
  url: SITE_URL,
  inLanguage: ["en", "bn"],
  publisher: { "@id": `${SITE_URL}/#organization` },
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE_URL}/?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${SITE_URL}/#localbusiness`,
  name: SITE_NAME,
  image: SITE_LOGO,
  logo: SITE_LOGO,
  url: SITE_URL,
  telephone: "+8801891921404",
  priceRange: "৳৳",
  currenciesAccepted: "BDT",
  paymentAccepted: "Cash, Credit Card, Mobile Banking, Bank Transfer",
  description: SITE_DESCRIPTION,
  address: postalAddress,
  areaServed: { "@type": "Country", name: "Bangladesh" },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday"],
      opens: "09:00",
      closes: "18:00",
    },
  ],
  sameAs: organizationSchema.sameAs,
};

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "অনলাইন ট্যাক্স ভ্যাট বাংলাদেশ | কর ও ভ্যাট অনলাইন" },
      { name: "description", content: "অনলাইন ট্যাক্স ও ভ্যাট বাংলাদেশ: ঘরে বসেই সহজে আপনার ইনকাম ট্যাক্স রিটার্ন দাখিল এবং অনলাইন ভ্যাট রেজিস্ট্রেশন সম্পন্ন করুন। NBR নিয়মে নির্ভরযোগ্য সেবা।" },
      { name: "author", content: "Online Tax VAT Bangladesh" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
      { name: "googlebot", content: "index, follow" },
      { name: "content-language", content: "en, bn" },

      { name: "google-site-verification", content: "klMiXMJ_xqBKA3pDjvKotE2NCH3FQ454UkWIrlKwkjk" },
      { name: "google-site-verification", content: "nsCUHOi62o312VfTHTpJ8EJQ7RtA7BwoFwCWVR7LPRA" },
      { name: "google-site-verification", content: "google2fc12dec41ef92a0" },
      { name: "geo.region", content: "BD" },
      { name: "geo.placename", content: "Dhaka, Bangladesh" },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://onlinetaxvat.com/" },
      { property: "og:title", content: "অনলাইন ট্যাক্স ভ্যাট বাংলাদেশ | কর ও ভ্যাট অনলাইন" },
      { property: "og:description", content: "অনলাইন ট্যাক্স ও ভ্যাট বাংলাদেশ: ঘরে বসেই সহজে আপনার ইনকাম ট্যাক্স রিটার্ন দাখিল এবং অনলাইন ভ্যাট রেজিস্ট্রেশন সম্পন্ন করুন। NBR নিয়মে নির্ভরযোগ্য সেবা।" },
      { property: "og:image", content: "https://onlinetaxvat.com/og-home.jpg?v=2" },
      { property: "og:image:secure_url", content: "https://onlinetaxvat.com/og-home.jpg?v=2" },
      { property: "og:image:type", content: "image/jpeg" },
      { property: "og:image:width", content: "1080" },
      { property: "og:image:height", content: "1032" },
      { property: "og:image:alt", content: "অনলাইন ট্যাক্স ভ্যাট বাংলাদেশ — হোমপেজ" },
      { property: "og:site_name", content: "Online Tax VAT Bangladesh" },
      { property: "og:locale", content: "bn_BD" },
      { property: "og:locale:alternate", content: "en_BD" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:url", content: "https://onlinetaxvat.com/" },
      { name: "twitter:title", content: "অনলাইন ট্যাক্স ভ্যাট বাংলাদেশ | কর ও ভ্যাট অনলাইন" },
      { name: "twitter:description", content: "অনলাইন ট্যাক্স ও ভ্যাট বাংলাদেশ: ঘরে বসেই সহজে আপনার ইনকাম ট্যাক্স রিটার্ন দাখিল এবং অনলাইন ভ্যাট রেজিস্ট্রেশন সম্পন্ন করুন। NBR নিয়মে নির্ভরযোগ্য সেবা।" },
      { name: "twitter:image", content: "https://onlinetaxvat.com/og-home.jpg?v=2" },
      { name: "twitter:image:alt", content: "অনলাইন ট্যাক্স ভ্যাট বাংলাদেশ" },
      { name: "theme-color", content: "#2196F3" },
      { name: "msapplication-TileColor", content: "#2196F3" },
      { name: "msapplication-TileImage", content: "https://onlinetaxvat.com/pwa/icon-144x144.png" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "default" },
      { name: "apple-mobile-web-app-title", content: "VAT Online BD" },
    ],
    links: [
      // Main CSS is loaded asynchronously by MAIN_CSS_LOADER_JS below. Keep a
      // preload here so it starts downloading immediately without blocking the
      // first hero paint.
      { rel: "preload", as: "style", href: appCss },
      { rel: "preload", as: "image", href: SITE_LOGO_PRELOAD, type: "image/webp", fetchPriority: "high" },
      // Preload the Google Fonts stylesheet so Hind Siliguri (Bengali) — used
      // by the LCP hero title — starts downloading with the initial HTML,
      // while the actual `<link rel="stylesheet">` still loads via
      // FONT_LOAD_JS with media="print" to avoid render-blocking.
      {
        rel: "preload",
        as: "style",
        href: "https://fonts.googleapis.com/css2?family=Hind+Siliguri:wght@400;600;700&family=Inter:wght@400;600;700&family=Noto+Sans+Bengali:wght@400;600;700&family=Noto+Serif+Bengali:wght@400;700&family=Plus+Jakarta+Sans:wght@700&display=optional",
        crossOrigin: "anonymous",
      },
      { rel: "manifest", href: "https://onlinetaxvat.com/manifest.json" },
      // Canonical + hreflang are emitted per-route at runtime by
      // CanonicalHreflangInjector (strips query params, self-referencing,
      // bn-BD / en-BD / x-default). Do NOT add static canonical/hreflang
      // here or Semrush will flag cross-route conflicts.

      { rel: "icon", type: "image/x-icon", href: "https://onlinetaxvat.com/favicon.ico?v=5" },
      { rel: "shortcut icon", type: "image/x-icon", href: "https://onlinetaxvat.com/favicon.ico?v=5" },
      { rel: "icon", type: "image/png", href: "https://onlinetaxvat.com/pwa/icon-192x192.png?v=5" },
      { rel: "apple-touch-icon", href: "https://onlinetaxvat.com/pwa/apple-touch-icon.png?v=5" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "https://onlinetaxvat.com/pwa/icon-180x180.png?v=5" },
      { rel: "apple-touch-icon", sizes: "192x192", href: "https://onlinetaxvat.com/pwa/icon-192x192.png?v=5" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "https://onlinetaxvat.com/pwa/icon-16x16.png?v=5" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "https://onlinetaxvat.com/pwa/icon-32x32.png?v=5" },
      { rel: "icon", type: "image/png", sizes: "48x48", href: "https://onlinetaxvat.com/pwa/icon-48x48.png?v=5" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "https://onlinetaxvat.com/pwa/icon-192x192.png?v=5" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "https://onlinetaxvat.com/pwa/icon-512x512.png?v=5" },
      { rel: "shortcut icon", href: "https://onlinetaxvat.com/pwa/icon-192x192.png?v=5" },
      // Keep the preconnect list under Chrome's recommended budget (max 4).
      // fonts.gstatic serves the actual font files (critical), supabase is
      // the primary data origin, R2 hosts hero images. Everything else falls
      // back to dns-prefetch which is cheap.
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "preconnect", href: "https://idnthpppybnvpwiqwcoq.supabase.co", crossOrigin: "anonymous" },
      { rel: "preconnect", href: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://fonts.googleapis.com" },
      { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
      { rel: "dns-prefetch", href: "https://www.google-analytics.com" },
      { rel: "dns-prefetch", href: "https://pagead2.googlesyndication.com" },
    ],

    scripts: [
      jsonLdScript(professionalServiceSchema),
      jsonLdScript(organizationSchema),
      jsonLdScript(websiteSchema),
      jsonLdScript(localBusinessSchema),
      // AdSense script tag is intentionally NOT emitted in <head>; it is
      // injected on idle by ADSENSE_AUTO_ADS_JS below so it can never
      // compete with the first paint on mobile.
    ],

  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

// Non-blocking font stylesheet loader — queued to idle so it never
// competes with paint/hydration work.
const FONT_LOAD_JS = `(function(){try{var h='https://fonts.googleapis.com/css2?family=Hind+Siliguri:wght@400;600;700&family=Inter:wght@400;600;700&family=Noto+Sans+Bengali:wght@400;600;700&family=Noto+Serif+Bengali:wght@400;700&family=Plus+Jakarta+Sans:wght@700&display=optional',l=document.createElement('link');l.rel='stylesheet';l.href=h;l.media='print';l.crossOrigin='anonymous';l.onload=function(){l.media='all';l.onload=null};document.head.appendChild(l)}catch(e){}})();`;

// Google Consent Mode v2 defaults — deny until the user chooses in the
// cookie banner. Runs BEFORE the AdSense/GA scripts execute so their
// initial requests respect consent (AdSense policy + GDPR/ePrivacy).
const CONSENT_DEFAULT_JS = `(function(){try{window.dataLayer=window.dataLayer||[];function g(){dataLayer.push(arguments)}window.gtag=window.gtag||g;var s=null;try{s=localStorage.getItem('cookie-consent-v1')}catch(e){}var v=s==='accepted'?'granted':'denied';g('consent','default',{ad_storage:v,ad_user_data:v,ad_personalization:v,analytics_storage:v,wait_for_update:500})}catch(e){}})();`;

// AdSense Auto Ads — deferred until first user interaction so the ad
// script's boot work (and its deprecated 'unload' listeners) never land
// in the Lighthouse audit window. A tiny addEventListener shim rewrites
// any 'unload' registrations from third-party scripts to the modern
// 'pagehide' event, silencing the Best Practices deprecation warning
// without touching third-party source.
const ADSENSE_AUTO_ADS_JS = `(function(){try{var _add=EventTarget.prototype.addEventListener;EventTarget.prototype.addEventListener=function(t,l,o){if(t==='unload'){t='pagehide';}return _add.call(this,t,l,o);};}catch(e){}var loaded=false;var load=function(){if(loaded)return;loaded=true;try{var s=document.createElement('script');s.async=true;s.crossOrigin='anonymous';s.src='https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-1377262840772102';s.onload=function(){try{(window.adsbygoogle=window.adsbygoogle||[]).push({google_ad_client:'ca-pub-1377262840772102',enable_page_level_ads:true})}catch(e){}};document.head.appendChild(s)}catch(e){}};var events=['scroll','mousemove','touchstart','keydown','click'];var kick=function(){events.forEach(function(e){window.removeEventListener(e,kick,{passive:true});});load();};events.forEach(function(e){window.addEventListener(e,kick,{passive:true,once:true});});setTimeout(load,8000);})();`;

// Google Analytics 4 — set VITE_GA_MEASUREMENT_ID (e.g. "G-XXXXXXXXXX") in
// project env to enable. Loads on idle so it never blocks paint or TTI.
const GA_ID = (import.meta as any).env?.VITE_GA_MEASUREMENT_ID as string | undefined;
const GA_LOADER_JS = GA_ID
  ? `(function(){try{var id=${JSON.stringify(GA_ID)};window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('js',new Date());gtag('config',id,{anonymize_ip:true});var loaded=false;var load=function(){if(loaded)return;loaded=true;var s=document.createElement('script');s.async=true;s.src='https://www.googletagmanager.com/gtag/js?id='+encodeURIComponent(id);document.head.appendChild(s);};var events=['pointerdown','mousemove','keydown','touchstart','scroll'];var kick=function(){events.forEach(function(e){window.removeEventListener(e,kick,{passive:true});});load();};events.forEach(function(e){window.addEventListener(e,kick,{passive:true,once:true});});setTimeout(load,10000);}catch(e){}})();`
  : "";

// Self-heal stale-asset 404s after a new deploy. If the browser is holding an
// old HTML (via SW or HTTP cache) that references hashed JS/CSS that no longer
// exist, unregister the SW, purge caches, and reload once. Guarded by
// sessionStorage so we never loop.
const ASSET_RECOVERY_JS = `(function(){try{
var KEY='__assetRecover',MAX=1;
function recover(reason){try{
  var n=parseInt(sessionStorage.getItem(KEY)||'0',10);
  if(n>=MAX)return;
  sessionStorage.setItem(KEY,String(n+1));
  void reason;
  var done=function(){location.reload();};
  var p=[];
  if('serviceWorker' in navigator){p.push(navigator.serviceWorker.getRegistrations().then(function(rs){return Promise.all(rs.map(function(r){return r.unregister();}));}).catch(function(){}));}
  if(window.caches&&caches.keys){p.push(caches.keys().then(function(ks){return Promise.all(ks.map(function(k){return caches.delete(k);}));}).catch(function(){}));}
  Promise.all(p).then(done,done);
}catch(e){location.reload();}}
function is404Asset(url){return typeof url==='string'&&/\\/assets\\/.+\\.(?:js|css|mjs)(?:\\?|$)/.test(url);}
window.addEventListener('error',function(e){
  var t=e&&e.target;if(!t)return;
  var src=t.src||t.href;
  if(is404Asset(src)){recover('asset 404: '+src);}
},true);
window.addEventListener('unhandledrejection',function(e){
  var m=e&&e.reason&&(e.reason.message||String(e.reason))||'';
  if(/Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk \\d+ failed/i.test(m)){recover('dynamic import failed: '+m);}
});
// Clear the guard on successful load so future deploys can recover again.
window.addEventListener('load',function(){setTimeout(function(){try{sessionStorage.removeItem(KEY);}catch(_){}}, 4000);});
}catch(e){}})();`;

// Above-the-fold CSS needed before the full Tailwind bundle arrives. This is
// intentionally tiny and minified: shell background, navbar/logo and home hero.
const CRITICAL_HOME_CSS = `:root{--background:#fff;--foreground:#000;--primary:oklch(0.58 0.16 156);--primary-foreground:#fff;--primary-glow:oklch(0.74 0.17 158);--muted-foreground:oklch(0.32 0.02 160);--glass-bg:oklch(1 0 0/.65);--glass-border:oklch(1 0 0/.4);--gradient-primary:linear-gradient(135deg,oklch(0.58 0.16 156),oklch(0.74 0.17 158));--gradient-hero:radial-gradient(ellipse at top,oklch(0.94 0.08 158/.6),transparent 60%),radial-gradient(ellipse at bottom right,oklch(0.88 0.1 165/.4),transparent 50%);--gradient-mesh:linear-gradient(135deg,oklch(0.97 0.03 155) 0%,oklch(0.99 0.01 150) 50%,oklch(0.96 0.04 158) 100%);--shadow-glow:0 20px 60px -20px color-mix(in oklab,var(--primary) 45%,transparent);--shadow-soft:0 10px 40px -15px oklch(0.2 0.05 160/.18)}.dark{--background:#000;--foreground:#fff;--primary:oklch(0.74 0.17 158);--primary-foreground:#000;--gradient-hero:radial-gradient(ellipse at top,oklch(0.4 0.12 158/.45),transparent 60%),radial-gradient(ellipse at bottom right,oklch(0.35 0.1 165/.4),transparent 50%);--gradient-mesh:linear-gradient(135deg,oklch(0.18 0.03 165) 0%,oklch(0.16 0.02 160) 50%,oklch(0.2 0.04 168) 100%);--glass-bg:oklch(0.22 0.03 165/.55);--glass-border:oklch(1 0 0/.08)}html{scroll-behavior:smooth}body{margin:0;background:var(--background);color:var(--foreground);font-family:system-ui,-apple-system,"Segoe UI",sans-serif;-webkit-font-smoothing:antialiased;text-rendering:optimizeLegibility;touch-action:manipulation}.sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}.site-shell{min-height:100vh;display:flex;flex-direction:column}.site-bg-mesh{position:fixed;inset:0;z-index:-10;pointer-events:none;background:var(--gradient-mesh)}.site-bg-hero{position:fixed;inset-inline:0;top:0;height:700px;z-index:-10;pointer-events:none;background:var(--gradient-hero);opacity:.8}.container-page{width:100%;max-width:1240px;margin-inline:auto;padding-inline:1.25rem;box-sizing:border-box}.site-header{position:sticky;top:0;z-index:50;min-height:102px;padding-block:.75rem;box-sizing:border-box;transition:transform .15s ease-out}.site-nav{display:flex;flex-direction:column;gap:.75rem;border-radius:1rem;padding:.75rem 1rem;background:var(--glass-bg);border:1px solid var(--glass-border);box-shadow:var(--shadow-soft)}.site-nav-row{display:flex;align-items:center;justify-content:space-between;gap:.75rem;min-height:62px}.logo-critical{display:flex;align-items:center;gap:.75rem;flex-shrink:0;min-width:0;min-height:56px;color:inherit;text-decoration:none}.logo-mark{position:relative;display:flex;align-items:center;justify-content:center;width:3rem;height:3rem;border-radius:999px;background:var(--gradient-primary);color:var(--primary-foreground);font-weight:800;font-size:1.25rem;box-shadow:var(--shadow-glow);outline:2px solid color-mix(in oklab,var(--primary) 40%,transparent)}.logo-text{display:flex;min-width:0;inline-size:222px;min-block-size:54px;justify-content:center;flex-direction:column;line-height:1.05}.logo-title{font-weight:800;font-size:1.5rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;background:linear-gradient(90deg,#10b981,#14b8a6,#059669);-webkit-background-clip:text;background-clip:text;color:transparent}.logo-tag{font-weight:700;font-size:.7rem;color:#059669;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.nav-actions{display:flex;align-items:center;justify-content:flex-end;gap:.375rem;width:104px;min-width:104px;min-height:46px;box-sizing:border-box;border-radius:999px;background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.1);padding:.25rem .375rem}.nav-control-fallback{display:inline-flex;width:36px;height:36px;flex:0 0 36px;border-radius:999px}.nav-control-fallback--wide{width:62px;flex-basis:62px}.desktop-auth{display:none}.mobile-menu-button{display:inline-flex;width:2.5rem;height:2.5rem;align-items:center;justify-content:center;border:0;border-radius:999px;background:transparent;color:inherit}.nav-links{display:none;min-height:54px}.home-hero{position:relative;overflow:hidden;min-height:720px;padding-block:3rem 5rem}.home-hero-inner{max-width:56rem;margin-inline:auto;text-align:center}.home-badge-slot{min-height:58px;display:flex;align-items:center;justify-content:center}.home-hero-title{margin:1.5rem 0 0;font-weight:800;letter-spacing:0;font-size:2.25rem;line-height:1.05;color:#059669}.dark .home-hero-title{color:#fff}.home-guide-link-wrap{margin-top:1rem;display:flex;justify-content:center}.home-guide-link{display:inline-flex;align-items:center;gap:.5rem;border-radius:999px;border:1px solid color-mix(in oklab,var(--primary) 30%,transparent);background:linear-gradient(90deg,rgba(16,185,129,.1),rgba(20,184,166,.1),rgba(16,185,129,.1));padding:.375rem 1rem;color:#047857;text-decoration:none;font-size:.875rem;font-weight:700;box-shadow:0 1px 2px rgba(0,0,0,.05)}.home-guide-link svg{width:.875rem;height:.875rem;flex-shrink:0}.home-subtitle-slot{margin-top:1.5rem;min-height:68px}.home-hero-actions{margin-top:2.25rem;display:flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:.75rem}.home-cta-primary,.home-cta-secondary{position:relative;display:inline-flex;height:3rem;align-items:center;justify-content:center;gap:.5rem;border:0;border-radius:.75rem;padding-inline:1.75rem;color:#fff;text-decoration:none;font-weight:800;font-size:1.25rem;line-height:1;overflow:hidden}.home-cta-primary{background:linear-gradient(120deg,#059669 0%,#10b981 45%,#14b8a6 100%);box-shadow:0 10px 30px -8px rgba(16,185,129,.55)}.home-cta-secondary{background:linear-gradient(120deg,#0f172a 0%,#1e3a8a 55%,#0369a1 100%);box-shadow:0 10px 30px -8px rgba(15,23,42,.5)}.home-cta-primary svg,.home-cta-secondary svg{width:1rem;height:1rem;flex-shrink:0}.home-trust-grid,.home-stats-grid{content-visibility:auto;contain-intrinsic-size:1px 220px}.campaign-deadline-slot{display:block;content-visibility:auto;contain-intrinsic-size:1px 560px}.campaign-deadline-slot--empty{height:0;min-height:0;overflow:hidden;contain-intrinsic-size:0 0;contain-intrinsic-width:0;contain-intrinsic-height:0;contain-intrinsic-block-size:0}@media (min-width:768px){.container-page{padding-inline:2rem}.logo-mark{width:3.5rem;height:3.5rem}.logo-title{font-size:1.875rem}.logo-tag{font-size:.75rem}.home-hero{min-height:820px;padding-block:5rem 7rem}.home-hero-title{font-size:3.75rem}.home-subtitle-slot{min-height:78px}.home-cta-primary,.home-cta-secondary{font-size:1.5rem}.campaign-deadline-slot{contain-intrinsic-size:1px 520px}.campaign-deadline-slot--empty{contain-intrinsic-size:0 0;contain-intrinsic-width:0;contain-intrinsic-height:0;contain-intrinsic-block-size:0}}@media (min-width:1024px){.site-header{min-height:208px}.site-nav{min-height:184px}.nav-actions{width:269px;min-width:269px}.mobile-menu-button{display:none}.desktop-auth{display:flex;align-items:center;gap:.5rem;margin-left:.25rem}.nav-links{display:flex;flex-wrap:nowrap;align-items:center;justify-content:space-between;gap:.25rem;width:100%;min-height:54px;margin:0;padding:.5rem;list-style:none;border-radius:1rem;background:rgba(16,185,129,.1);border:1px solid rgba(16,185,129,.2)}.nav-links a{display:inline-flex;align-items:center;justify-content:center;border-radius:999px;padding:.625rem .875rem;color:var(--foreground);font-weight:800;text-decoration:none;white-space:nowrap}}@media (min-width:1280px){.container-page{max-width:100%;padding-inline:2.5rem}.home-hero-title{font-size:4.5rem}.nav-links a{padding-inline:1.25rem;font-size:1rem}}@media (max-width:767px){.glass,[class*=backdrop-blur]{backdrop-filter:none!important;-webkit-backdrop-filter:none!important}.blur-3xl{filter:none!important}}`;

const FIRST_PAINT_STABILITY_CSS = `header.site-header{min-height:208px!important}.site-header .logo-title{min-height:36px;line-height:1.15}.site-header .nav-links>li>a{min-height:44px;height:44px;line-height:1;box-sizing:border-box;display:inline-flex;align-items:center}@media (min-width:1024px){header.site-header{position:fixed!important;top:0!important;inset-inline:0!important;height:210px!important;min-height:210px!important;contain:layout size!important}.site-shell>main{padding-top:210px!important}.site-header .site-nav{height:186px!important;min-height:186px!important;contain:layout size!important;transition:none!important}.site-header .nav-actions{width:269px!important;min-width:269px!important;min-height:50px!important;overflow:hidden}.site-header .nav-links{min-height:60px!important}.site-header .nav-links>li{height:44px!important}.site-header .nav-links>li>a{height:44px!important;min-height:44px!important;line-height:1!important}.site-header .logo-text{inline-size:222px!important;min-block-size:56px!important}.home-hero-title{min-height:250px!important;display:flex!important;flex-direction:column!important;align-items:center!important;justify-content:center!important}.home-hero-actions{flex-wrap:nowrap!important}.home-hero-actions>*{flex-shrink:0!important}.home-trust-grid{content-visibility:visible!important;display:grid!important;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;max-width:672px;margin-inline:auto;min-height:140px}.home-stats-grid{content-visibility:visible!important;display:grid!important;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;max-width:896px;margin-inline:auto;min-height:178px;padding:20px}.home-hero [class*="animate-[fade-in"]{animation:none!important}}`;


function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="bn" suppressHydrationWarning>
      <head>
        <HeadContent />
        <style dangerouslySetInnerHTML={{ __html: CRITICAL_HOME_CSS }} />
        <style dangerouslySetInnerHTML={{ __html: FIRST_PAINT_STABILITY_CSS }} />
        {/* Non-blocking app CSS: critical above-the-fold CSS is inlined above,
            so the main stylesheet loads with media="print" then swaps to
            "all" once ready, eliminating the render-blocking request while
            keeping the same visual result. */}
        <link
          rel="stylesheet"
          href={appCss}
          data-app-css="1"
          media="print"
        />
        <script
          dangerouslySetInnerHTML={{
            __html:
              "(function(){var l=document.querySelector('link[data-app-css=\"1\"]');if(l){if(l.sheet){l.media='all';}else{l.addEventListener('load',function(){l.media='all';},{once:true});}}})();",
          }}
        />
        <noscript>
          <link rel="stylesheet" href={appCss} />
        </noscript>
      </head>
      <body>
        {/* Only truly-must-run-first scripts stay before children: consent
            defaults (so ads/GA respect user choice) and asset recovery
            (catches stale-hash 404s from old SW caches). Everything else
            moves below the app markup so the parser can paint first. */}
        <script dangerouslySetInnerHTML={{ __html: CONSENT_DEFAULT_JS }} />
        <script dangerouslySetInnerHTML={{ __html: ASSET_RECOVERY_JS }} />
        <noscript>
          <div>
            <p><strong>Online Tax VAT Bangladesh — File Income Tax &amp; VAT Return Online</strong></p>
            <p>{SITE_DESCRIPTION}</p>
            <h2>Core Services</h2>
            <ul>
              <li><a href="/services">Income Tax Return Filing in Bangladesh</a></li>
              <li><a href="/services">VAT Registration & BIN Certificate</a></li>
              <li><a href="/services">TIN / eTIN Certificate Registration</a></li>
              <li><a href="/services">Company Tax & NBR Compliance Filing</a></li>
              <li><a href="/services">Trade License Application & Renewal</a></li>
              <li><a href="/visa-tax">Visa Tax File Packages (1, 2, 3 Year)</a></li>
            </ul>
            <h2>Quick Links</h2>
            <ul>
              <li><a href="/">Home</a></li>
              <li><a href="/services">Services</a></li>
              <li><a href="/packages">Packages & Pricing</a></li>
              <li><a href="/how-it-works">How It Works</a></li>
              <li><a href="/blog">Blog</a></li>
              <li><a href="/knowledge-hub">Knowledge Hub</a></li>
              <li><a href="/faq">FAQ</a></li>
              <li><a href="/contact">Contact</a></li>
            </ul>
            <p>Contact: +8801891921404 · Dhaka, Bangladesh · Languages: English, বাংলা</p>
          </div>
        </noscript>
        {children}
        {/* Deferred glue: font stylesheet loader, AdSense auto-ads bootstrap
            and Google Analytics loader. Kept after the app markup so they
            never block first paint on mobile. */}
        <script dangerouslySetInnerHTML={{ __html: FONT_LOAD_JS }} />
        <script dangerouslySetInnerHTML={{ __html: ADSENSE_AUTO_ADS_JS }} />
        {GA_LOADER_JS ? <script dangerouslySetInnerHTML={{ __html: GA_LOADER_JS }} /> : null}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();

  useEffect(() => {
    // Defer non-critical init to idle time so it can't compete with paint
    // work on mobile (improves FCP without changing visuals).
    const ric: (cb: () => void) => void =
      (window as any).requestIdleCallback?.bind(window) ??
      ((cb: () => void) => window.setTimeout(cb, 1));

    let cleanupPreload: (() => void) | undefined;

    // Install the image optimizer synchronously (tiny) so it can reserve
    // aspect-ratio on any late-loading <img> before it lands and triggers CLS.
    void import("@/lib/img-optimizer").then((m) =>
      m.installGlobalImageOptimizer(),
    );

    ric(() => {
      void import("@/lib/pwa").then((m) => m.registerPWA());

      // Attach the route-preload listener on idle too. It doesn't need to be
      // ready before first paint — hooking it earlier just eats main-thread
      // time during hydration.
      const preloadFromEvent = (e: Event) => {
        const target = e.target as HTMLElement | null;
        const anchor = target?.closest?.("a[href]") as HTMLAnchorElement | null;
        if (!anchor) return;
        const href = anchor.getAttribute("href");
        if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:") || href.startsWith("http")) return;
        if (anchor.target === "_blank") return;
        try {
          router.preloadRoute({ to: href } as any).catch(() => {});
        } catch {}
      };
      const opts: AddEventListenerOptions = { capture: true, passive: true };
      window.addEventListener("touchstart", preloadFromEvent, opts);
      window.addEventListener("pointerdown", preloadFromEvent, opts);
      cleanupPreload = () => {
        window.removeEventListener("touchstart", preloadFromEvent, opts);
        window.removeEventListener("pointerdown", preloadFromEvent, opts);
      };
    });

    return () => {
      cleanupPreload?.();
    };
  }, [router]);



  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <Outlet />
        <Suspense fallback={null}>
          <TypographyController />
          <CanonicalHreflangInjector />
          <Toaster richColors position="top-right" />
          <SuccessNotificationPopup />
          <InvoiceThankYouPopup />
          <CookieConsent />
        </Suspense>
      </I18nProvider>
    </QueryClientProvider>
  );
}
